import {
  PercentsRowProps,
  ProgressesAvgForMunicipality,
  ProgressesAvgForRegion,
  ProgressesForMunicipality,
  ProgressesForRegion,
  ProgressesParallelsForMunicipality,
  ProgressesParallelsForRegion,
  Subject,
  Test,
  TestProgressesAvg,
  TestProgressesParallels,
  TestProgressesTotal,
  TestRound,
  TotalPercentsRowProps,
} from 'features/monitoring/models';
import { useEffect, useMemo, useState } from 'react';
import { KnowledgeMonitoringApi } from 'api';
import { collectRows } from 'features/monitoring/collectRows';

function getTotal(total: TestProgressesTotal, targetTestId: number) {
  const targetTotalByParallels = (total.parallels_completion_percents || []).filter(
    (sample) => sample.test_id === targetTestId,
  );
  const targetAverageTotal = (total.avg_completion_percents || []).find((sample) => sample.test_id === targetTestId);

  const result = {
    percentsByParallels: {},
    averagePercent: Math.round((targetAverageTotal?.avg_completion_percent || 0) * 10) / 10,
    registeredStudentsCount: targetAverageTotal?.registered_students_count || 0,
    testedStudentsCount: targetAverageTotal?.tested_students_count || 0,
  } as TotalPercentsRowProps;

  targetTotalByParallels.forEach((sample) => {
    result.percentsByParallels[sample.parallel] = Math.round(sample.completion_percent * 10) / 10;
  });

  return result;
}

function getNewRowBase(averageSample: TestProgressesAvg) {
  return {
    testedStudentsCount: averageSample.tested_students_count ?? 0,
    registeredStudentsCount: averageSample.registered_students_count ?? 0,
    averagePercent: Math.round(averageSample.avg_completion_percent * 10) / 10,
    percentsByParallels: {},
    noResults: true,
  };
}

function getNewMunicipalityRow(sample: ProgressesAvgForRegion): PercentsRowProps {
  return {
    ...getNewRowBase(sample),
    school: '',
    location: sample.municipality_name,
  };
}

function getNewSchoolRow(sample: ProgressesAvgForMunicipality) {
  return {
    ...getNewRowBase(sample),
    school: sample.school_name,
    location: sample.school_location,
  };
}

function updatePercents(school: PercentsRowProps, sample: TestProgressesParallels) {
  school.percentsByParallels[sample.parallel] = Math.round(sample.completion_percent * 10) / 10;
  if (!!sample.completion_percent) {
    school.noResults = false;
  }
}

function getRows(progresses: ProgressesForMunicipality | ProgressesForRegion, isRegion: boolean, targetTestId: number) {
  if (isRegion) {
    return collectRows(
      (progresses.parallels_completion_percents || []) as ProgressesParallelsForRegion[],
      (sample) => sample.test_id === targetTestId,
      (sample) => {
        const avg = ((progresses.avg_completion_percents || []) as ProgressesAvgForRegion[]).find(
          (s) => s.municipality_id === sample.municipality_id,
        );
        return avg ? getNewMunicipalityRow(avg) : null;
      },
      (sample) => sample.municipality_id,
      updatePercents,
    );
  } else {
    return collectRows(
      (progresses.parallels_completion_percents || []) as ProgressesParallelsForMunicipality[],
      (sample) => sample.test_id === targetTestId,
      (sample) => {
        const avg = ((progresses.avg_completion_percents || []) as ProgressesAvgForMunicipality[]).find(
          (s) => s.school_id === sample.school_id,
        );
        return avg ? getNewSchoolRow(avg) : null;
      },
      (sample) => sample.school_id,
      updatePercents,
    );
  }
}

export const usePercentsTableProps = (isRegion: boolean, tests: Test[], subject: Subject, testRound: TestRound) => {
  const [progresses, setProgresses] = useState<ProgressesForMunicipality | ProgressesForRegion>();

  useEffect(() => {
    async function fetch() {
      const progresses = isRegion
        ? await KnowledgeMonitoringApi.getTestProgressesForRegion()
        : await KnowledgeMonitoringApi.getTestProgressesForMunicipality();

      setProgresses(progresses);
    }

    fetch();
  }, []);

  return useMemo(() => {
    if (!progresses || !tests || !progresses.avg_completion_percents || !progresses.parallels_completion_percents) {
      return null;
    }

    const targetTestId = tests.find((test) => test.subject === subject && test.test_name === testRound)?.test_id;

    if(!targetTestId) {
      return {
        total: {
          percentsByParallels: {},
          averagePercent: 0,
          registeredStudentsCount: 0,
          testedStudentsCount: 0,
        },
        rows: [],
      }
    }

    const rows = getRows(progresses, isRegion, targetTestId);

    const total = getTotal(progresses.total, targetTestId);

    return {
      total,
      rows,
    };
  }, [tests, progresses, subject, testRound]);
};
